/**
 * =============================================================================
 * FALCON - Module Deneigement des Parkings Avion
 * =============================================================================
 *
 * @file        services/offlineDb.ts
 * @description Service IndexedDB pour le stockage offline des donnees
 *              Permet aux agents de travailler sans connexion internet
 *
 * @version     2.4
 * @author      Equipe FALCON
 * =============================================================================
 */

import { openDB, DBSchema, IDBPDatabase } from 'idb';
import type { Deneignement, DeneignementZone } from '@/types';

// =============================================================================
// TYPES
// =============================================================================

export type PendingActionType =
  | 'zone_start'
  | 'zone_end'
  | 'intervention_start'
  | 'intervention_end'
  | 'photo_upload'
  | 'incident_report';

export interface PendingAction {
  id?: number;
  type: PendingActionType;
  payload: Record<string, unknown>;
  timestamp: string;
  synced: boolean;
  retryCount: number;
  lastError?: string;
}

export interface CachedIntervention extends Deneignement {
  cachedAt: string;
}

export interface CachedZone extends DeneignementZone {
  deneignementId: number;
  cachedAt: string;
}

// =============================================================================
// DATABASE SCHEMA
// =============================================================================

interface FalconOfflineDB extends DBSchema {
  interventions: {
    key: number;
    value: CachedIntervention;
    indexes: {
      'by-statut': string;
      'by-date': string;
    };
  };
  zones: {
    key: number;
    value: CachedZone;
    indexes: {
      'by-deneignement': number;
      'by-statut': string;
    };
  };
  pendingActions: {
    key: number;
    value: PendingAction;
    indexes: {
      'by-synced': number;
      'by-timestamp': string;
      'by-type': string;
    };
  };
  metadata: {
    key: string;
    value: {
      key: string;
      value: unknown;
      updatedAt: string;
    };
  };
}

// =============================================================================
// DATABASE SINGLETON
// =============================================================================

const DB_NAME = 'falcon-offline';
const DB_VERSION = 1;

let dbInstance: IDBPDatabase<FalconOfflineDB> | null = null;

export async function getDB(): Promise<IDBPDatabase<FalconOfflineDB>> {
  if (dbInstance) {
    return dbInstance;
  }

  dbInstance = await openDB<FalconOfflineDB>(DB_NAME, DB_VERSION, {
    upgrade(db) {
      // Interventions store
      if (!db.objectStoreNames.contains('interventions')) {
        const interventionStore = db.createObjectStore('interventions', { keyPath: 'id' });
        interventionStore.createIndex('by-statut', 'statut');
        interventionStore.createIndex('by-date', 'dateIntervention');
      }

      // Zones store
      if (!db.objectStoreNames.contains('zones')) {
        const zoneStore = db.createObjectStore('zones', { keyPath: 'id' });
        zoneStore.createIndex('by-deneignement', 'deneignementId');
        zoneStore.createIndex('by-statut', 'statut');
      }

      // Pending actions store
      if (!db.objectStoreNames.contains('pendingActions')) {
        const actionsStore = db.createObjectStore('pendingActions', {
          keyPath: 'id',
          autoIncrement: true,
        });
        // Note: IndexedDB stores booleans, but indexes work better with numbers
        actionsStore.createIndex('by-synced', 'synced');
        actionsStore.createIndex('by-timestamp', 'timestamp');
        actionsStore.createIndex('by-type', 'type');
      }

      // Metadata store for app state
      if (!db.objectStoreNames.contains('metadata')) {
        db.createObjectStore('metadata', { keyPath: 'key' });
      }
    },
    blocked() {
      console.warn('IndexedDB upgrade blocked - close other tabs');
    },
    blocking() {
      // Close current connection to allow upgrade in other tab
      dbInstance?.close();
      dbInstance = null;
    },
    terminated() {
      dbInstance = null;
    },
  });

  return dbInstance;
}

// =============================================================================
// INTERVENTION OPERATIONS
// =============================================================================

export async function cacheIntervention(intervention: Deneignement): Promise<void> {
  const db = await getDB();
  const cached: CachedIntervention = {
    ...intervention,
    cachedAt: new Date().toISOString(),
  };
  await db.put('interventions', cached);
}

export async function cacheInterventions(interventions: Deneignement[]): Promise<void> {
  const db = await getDB();
  const tx = db.transaction('interventions', 'readwrite');
  const now = new Date().toISOString();

  await Promise.all([
    ...interventions.map((i) =>
      tx.store.put({ ...i, cachedAt: now })
    ),
    tx.done,
  ]);
}

export async function getCachedIntervention(id: number): Promise<CachedIntervention | undefined> {
  const db = await getDB();
  return db.get('interventions', id);
}

export async function getCachedInterventions(): Promise<CachedIntervention[]> {
  const db = await getDB();
  return db.getAll('interventions');
}

export async function getCachedInterventionsByStatut(statut: string): Promise<CachedIntervention[]> {
  const db = await getDB();
  return db.getAllFromIndex('interventions', 'by-statut', statut);
}

export async function deleteCachedIntervention(id: number): Promise<void> {
  const db = await getDB();
  await db.delete('interventions', id);
}

export async function clearCachedInterventions(): Promise<void> {
  const db = await getDB();
  await db.clear('interventions');
}

// =============================================================================
// ZONE OPERATIONS
// =============================================================================

export async function cacheZone(zone: DeneignementZone, deneignementId: number): Promise<void> {
  const db = await getDB();
  const cached: CachedZone = {
    ...zone,
    deneignementId,
    cachedAt: new Date().toISOString(),
  };
  await db.put('zones', cached);
}

export async function cacheZones(zones: DeneignementZone[], deneignementId: number): Promise<void> {
  const db = await getDB();
  const tx = db.transaction('zones', 'readwrite');
  const now = new Date().toISOString();

  await Promise.all([
    ...zones.map((z) =>
      tx.store.put({ ...z, deneignementId, cachedAt: now })
    ),
    tx.done,
  ]);
}

export async function getCachedZone(id: number): Promise<CachedZone | undefined> {
  const db = await getDB();
  return db.get('zones', id);
}

export async function getCachedZonesForIntervention(deneignementId: number): Promise<CachedZone[]> {
  const db = await getDB();
  return db.getAllFromIndex('zones', 'by-deneignement', deneignementId);
}

export async function updateCachedZoneStatut(
  zoneId: number,
  statut: string,
  heureDebut?: string,
  heureFin?: string,
  commentaire?: string
): Promise<void> {
  const db = await getDB();
  const zone = await db.get('zones', zoneId);
  if (zone) {
    zone.statut = statut as CachedZone['statut'];
    if (heureDebut) zone.heureDebut = heureDebut;
    if (heureFin) zone.heureFin = heureFin;
    if (commentaire !== undefined) zone.commentaire = commentaire;
    zone.cachedAt = new Date().toISOString();
    await db.put('zones', zone);
  }
}

export async function clearCachedZones(): Promise<void> {
  const db = await getDB();
  await db.clear('zones');
}

// =============================================================================
// PENDING ACTIONS OPERATIONS
// =============================================================================

export async function queuePendingAction(
  type: PendingActionType,
  payload: Record<string, unknown>
): Promise<number> {
  const db = await getDB();
  const action: Omit<PendingAction, 'id'> = {
    type,
    payload,
    timestamp: new Date().toISOString(),
    synced: false,
    retryCount: 0,
  };
  return db.add('pendingActions', action as PendingAction);
}

export async function getPendingActions(): Promise<PendingAction[]> {
  const db = await getDB();
  // Get all unsynced actions
  const allActions = await db.getAll('pendingActions');
  return allActions.filter((a) => !a.synced);
}

export async function getPendingActionCount(): Promise<number> {
  const db = await getDB();
  const allActions = await db.getAll('pendingActions');
  return allActions.filter((a) => !a.synced).length;
}

export async function markActionSynced(id: number): Promise<void> {
  const db = await getDB();
  const action = await db.get('pendingActions', id);
  if (action) {
    action.synced = true;
    await db.put('pendingActions', action);
  }
}

export async function markActionFailed(id: number, error: string): Promise<void> {
  const db = await getDB();
  const action = await db.get('pendingActions', id);
  if (action) {
    action.retryCount += 1;
    action.lastError = error;
    await db.put('pendingActions', action);
  }
}

export async function deleteAction(id: number): Promise<void> {
  const db = await getDB();
  await db.delete('pendingActions', id);
}

export async function clearSyncedActions(): Promise<void> {
  const db = await getDB();
  const tx = db.transaction('pendingActions', 'readwrite');
  const allActions = await tx.store.getAll();

  await Promise.all([
    ...allActions.filter((a) => a.synced).map((a) => tx.store.delete(a.id!)),
    tx.done,
  ]);
}

export async function clearAllPendingActions(): Promise<void> {
  const db = await getDB();
  await db.clear('pendingActions');
}

// =============================================================================
// METADATA OPERATIONS
// =============================================================================

export async function setMetadata(key: string, value: unknown): Promise<void> {
  const db = await getDB();
  await db.put('metadata', {
    key,
    value,
    updatedAt: new Date().toISOString(),
  });
}

export async function getMetadata<T>(key: string): Promise<T | undefined> {
  const db = await getDB();
  const record = await db.get('metadata', key);
  return record?.value as T | undefined;
}

export async function deleteMetadata(key: string): Promise<void> {
  const db = await getDB();
  await db.delete('metadata', key);
}

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

export async function clearAllData(): Promise<void> {
  const db = await getDB();
  await Promise.all([
    db.clear('interventions'),
    db.clear('zones'),
    db.clear('pendingActions'),
    db.clear('metadata'),
  ]);
}

export async function getDatabaseStats(): Promise<{
  interventions: number;
  zones: number;
  pendingActions: number;
  syncedActions: number;
}> {
  const db = await getDB();
  const [interventions, zones, allActions] = await Promise.all([
    db.count('interventions'),
    db.count('zones'),
    db.getAll('pendingActions'),
  ]);

  return {
    interventions,
    zones,
    pendingActions: allActions.filter((a) => !a.synced).length,
    syncedActions: allActions.filter((a) => a.synced).length,
  };
}

export async function closeDB(): Promise<void> {
  if (dbInstance) {
    dbInstance.close();
    dbInstance = null;
  }
}
